import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { pageTransition } from '../../utils/pageTransitions';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import { notificationService } from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';
import NotificationCard from './components/NotificationCard';
import NotificationFilters from './components/NotificationFilters';
import NotificationStats from './components/NotificationStats';

const LiveNotificationCenter = () => {
  const { user, userProfile } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: '',
    priority: '',
    status: '',
    dateRange: 'all',
  });
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    urgent: 0,
    pending: 0,
  });

  useEffect(() => {
    loadNotifications();
    setupRealtimeSubscriptions();
  }, []);

  useEffect(() => {
    updateStats();
  }, [notifications]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationService?.getNotificationLogs({ limit: 100 });
      setNotifications(data || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscriptions = () => {
    // Subscribe to notification logs
    const unsubscribeLogs = notificationService?.subscribeToNotificationLogs((payload) => {
      handleRealtimeUpdate(payload);
    });

    // Subscribe to quotes (quote requests)
    const unsubscribeQuotes = notificationService?.subscribeToQuotes((payload) => {
      if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
        createNotificationFromQuote(payload?.new);
      }
    });

    // Subscribe to project milestones
    const unsubscribeMilestones = notificationService?.subscribeToProjectMilestones((payload) => {
      if (payload?.eventType === 'UPDATE' && payload?.new?.status === 'completed') {
        createNotificationFromMilestone(payload?.new);
      }
    });

    // Subscribe to team schedules (assignments)
    const unsubscribeTeam = notificationService?.subscribeToTeamSchedules((payload) => {
      if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
        createNotificationFromTeamSchedule(payload?.new);
      }
    });

    // Subscribe to orders (payment confirmations)
    const unsubscribeOrders = notificationService?.subscribeToOrders((payload) => {
      if (payload?.eventType === 'UPDATE' && payload?.new?.payment_status === 'succeeded') {
        createNotificationFromOrder(payload?.new);
      }
    });

    // Cleanup subscriptions
    return () => {
      unsubscribeLogs?.();
      unsubscribeQuotes?.();
      unsubscribeMilestones?.();
      unsubscribeTeam?.();
      unsubscribeOrders?.();
    };
  };

  const handleRealtimeUpdate = (payload) => {
    if (payload?.eventType === 'INSERT') {
      const newNotification = {
        id: payload?.new?.id,
        notificationType: payload?.new?.notification_type,
        recipientEmail: payload?.new?.recipient_email,
        recipientName: payload?.new?.recipient_name,
        subject: payload?.new?.subject,
        message: payload?.new?.message,
        status: payload?.new?.status,
        sentAt: payload?.new?.sent_at,
        readAt: payload?.new?.read_at,
        referenceType: payload?.new?.reference_type,
        referenceId: payload?.new?.reference_id,
        metadata: payload?.new?.metadata,
        createdAt: payload?.new?.created_at,
      };
      setNotifications((prev) => [newNotification, ...prev]);
    } else if (payload?.eventType === 'UPDATE') {
      setNotifications((prev) =>
        prev?.map((notif) =>
          notif?.id === payload?.new?.id
            ? {
                ...notif,
                status: payload?.new?.status,
                readAt: payload?.new?.read_at,
              }
            : notif
        )
      );
    } else if (payload?.eventType === 'DELETE') {
      setNotifications((prev) => prev?.filter((notif) => notif?.id !== payload?.old?.id));
    }
  };

  const createNotificationFromQuote = (quote) => {
    const notification = {
      id: `quote-${quote?.id}`,
      notificationType: 'quote_request',
      subject: `Nouvelle demande de devis: ${quote?.title}`,
      message: `Un nouveau devis a été créé avec le statut: ${quote?.status}`,
      status: 'pending',
      priority: 'standard',
      referenceType: 'quote',
      referenceId: quote?.id,
      createdAt: quote?.created_at || new Date()?.toISOString(),
    };
    setNotifications((prev) => [notification, ...prev]);
  };

  const createNotificationFromMilestone = (milestone) => {
    const notification = {
      id: `milestone-${milestone?.id}`,
      notificationType: 'milestone_completed',
      subject: `Jalon complété: ${milestone?.title}`,
      message: `Le jalon "${milestone?.title}" a été marqué comme complété`,
      status: 'pending',
      priority: 'standard',
      referenceType: 'milestone',
      referenceId: milestone?.id,
      createdAt: milestone?.completed_at || new Date()?.toISOString(),
    };
    setNotifications((prev) => [notification, ...prev]);
  };

  const createNotificationFromTeamSchedule = (schedule) => {
    const notification = {
      id: `schedule-${schedule?.id}`,
      notificationType: 'team_assignment',
      subject: `Nouvelle assignation: ${schedule?.assignment_title}`,
      message: `Vous avez été assigné à: ${schedule?.assignment_title}`,
      status: 'pending',
      priority: schedule?.priority === 'urgent' ? 'urgent' : 'standard',
      referenceType: 'team_schedule',
      referenceId: schedule?.id,
      createdAt: schedule?.created_at || new Date()?.toISOString(),
    };
    setNotifications((prev) => [notification, ...prev]);
  };

  const createNotificationFromOrder = (order) => {
    const notification = {
      id: `order-${order?.id}`,
      notificationType: 'payment_confirmation',
      subject: `Paiement confirmé: ${order?.order_number}`,
      message: `Le paiement de ${order?.total_amount} ${order?.currency} a été confirmé`,
      status: 'pending',
      priority: 'standard',
      referenceType: 'order',
      referenceId: order?.id,
      createdAt: order?.updated_at || new Date()?.toISOString(),
    };
    setNotifications((prev) => [notification, ...prev]);
  };

  const updateStats = () => {
    const unread = notifications?.filter((n) => n?.status !== 'read')?.length || 0;
    const urgent = notifications?.filter((n) => n?.priority === 'urgent' || n?.notificationType === 'urgent_alert')?.length || 0;
    const pending = notifications?.filter((n) => n?.status === 'pending')?.length || 0;

    setStats({
      total: notifications?.length || 0,
      unread,
      urgent,
      pending,
    });
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService?.markAsRead(notificationId);
      setNotifications((prev) =>
        prev?.map((notif) =>
          notif?.id === notificationId ? { ...notif, status: 'read', readAt: new Date()?.toISOString() } : notif
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleBatchMarkAsRead = async () => {
    if (selectedNotifications?.length === 0) return;

    try {
      await notificationService?.batchMarkAsRead(selectedNotifications);
      setNotifications((prev) =>
        prev?.map((notif) =>
          selectedNotifications?.includes(notif?.id) ? { ...notif, status: 'read', readAt: new Date()?.toISOString() } : notif
        )
      );
      setSelectedNotifications([]);
    } catch (error) {
      console.error('Error batch marking notifications as read:', error);
    }
  };

  const handleSelectNotification = (notificationId) => {
    setSelectedNotifications((prev) =>
      prev?.includes(notificationId) ? prev?.filter((id) => id !== notificationId) : [...prev, notificationId]
    );
  };

  const handleSelectAll = () => {
    if (selectedNotifications?.length === filteredNotifications?.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(filteredNotifications?.map((n) => n?.id));
    }
  };

  const filteredNotifications = notifications?.filter((notification) => {
    if (filters?.type && notification?.notificationType !== filters?.type) return false;
    if (filters?.priority && notification?.priority !== filters?.priority) return false;
    if (filters?.status && notification?.status !== filters?.status) return false;
    if (filters?.dateRange !== 'all') {
      const notifDate = new Date(notification?.createdAt);
      const now = new Date();
      const daysDiff = Math?.floor((now - notifDate) / (1000 * 60 * 60 * 24));

      if (filters?.dateRange === 'today' && daysDiff > 0) return false;
      if (filters?.dateRange === 'week' && daysDiff > 7) return false;
      if (filters?.dateRange === 'month' && daysDiff > 30) return false;
    }
    return true;
  });

  return (
    <motion.div {...pageTransition} className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="Bell" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-xl md:text-3xl font-bold text-foreground">Centre de Notifications en Direct</h1>
                <p className="text-muted-foreground">Mises à jour instantanées sur vos opérations ORAZEK</p>
              </div>
            </div>
          </div>

          {/* Stats */}
          <NotificationStats stats={stats} />

          {/* Filters and Actions */}
          <div className="bg-card rounded-lg border border-border p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
              <NotificationFilters filters={filters} setFilters={setFilters} />
              <div className="flex flex-wrap items-center gap-2">
                {selectedNotifications?.length > 0 && (
                  <button
                    onClick={handleBatchMarkAsRead}
                    className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
                  >
                    <Icon name="Check" size={16} />
                    Marquer comme lu ({selectedNotifications?.length})
                  </button>
                )}
                <button
                  onClick={handleSelectAll}
                  className="px-3 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors flex items-center gap-2"
                >
                  <Icon name="CheckSquare" size={16} />
                  {selectedNotifications?.length === filteredNotifications?.length ? 'Désélectionner tout' : 'Sélectionner tout'}
                </button>
              </div>
            </div>
          </div>

          {/* Notifications List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : filteredNotifications?.length === 0 ? (
            <div className="bg-card rounded-lg border border-border p-12 text-center">
              <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground text-lg">Aucune notification trouvée</p>
              <p className="text-muted-foreground text-sm mt-2">Les nouvelles notifications apparaîtront ici en temps réel</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredNotifications?.map((notification) => (
                <NotificationCard
                  key={notification?.id}
                  notification={notification}
                  isSelected={selectedNotifications?.includes(notification?.id)}
                  onSelect={handleSelectNotification}
                  onMarkAsRead={handleMarkAsRead}
                />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </motion.div>
  );
};

export default LiveNotificationCenter;